import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from 'react';
import { createContext, useCallback, useContext } from 'react';

import { HERO_FORM_ANCHOR } from '../constants';

interface TrialDialogContextValue {
  /** Opens the enrolment dialog. Returns false when it could not (the caller falls back to the anchor). */
  openTrial: () => boolean;
}

export const TrialDialogContext = createContext<TrialDialogContextValue>({ openTrial: () => false });

export function useTrialDialog(): TrialDialogContextValue {
  return useContext(TrialDialogContext);
}

interface TrialCtaProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  children: ReactNode;
  /** Where the link lands without the dialog (a modified click, or no `showModal`). */
  anchor?: string;
  /** Runs before the dialog opens (the header closes its sheet). */
  onBeforeOpen?: () => void;
}

/**
 * Every "Testar grátis" on the page: a real link to the enrolment card, upgraded to open the trial dialog.
 * A modified click (new tab, new window) keeps the link's own behaviour.
 */
export function TrialCta({ children, anchor = HERO_FORM_ANCHOR, onBeforeOpen, onClick, ...anchorProps }: TrialCtaProps) {
  const { openTrial } = useTrialDialog();
  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      onBeforeOpen?.();
      if (openTrial()) event.preventDefault();
    },
    [onBeforeOpen, onClick, openTrial]
  );
  return (
    <a href={`#${anchor}`} data-np-trial='' onClick={handleClick} {...anchorProps}>
      {children}
    </a>
  );
}
